import type { UseLocalStorageReducerDispatch } from "@dealer-center/core-components";
import {
  Button,
  HeartIcon,
  HeartSolidIcon,
  ListItem,
  Tooltip,
  TrashIcon,
  useLocalStorageReducerActions
} from "@dealer-center/core-components";
import styled from "styled-components";
import type { dealerSearchIncoming } from "@dealer-center/ddc-pages-account-search-interface";
import { verticalTypeMapping } from "@dealer-center/ddc-pages-account-search-interface";
import { useContext } from "react";
import { AccountSearchContext } from "@dealer-center/ddc-pages-account-search-context";

const StyledItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const StyledAccountName = styled.span`
  flex-grow: 1;
  cursor: pointer;
`;
const StyledDeleteButton = styled(Button)`
  color: #dd2b2b;
  :hover {
    color: #c42525;
    background-color: transparent;
    border-color: transparent;
  }
`;
const StyledSearchInformation = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: 8px;
`;
const StyledSearchInformationItem = styled.span`
  margin-left: 8px;
  font-size: 12px;
  color: #6c757d;
`;
const StyledSearchInformationTitle = styled(StyledSearchInformationItem)``;
const StyledSearchInformationValue = styled(StyledSearchInformationItem)`
  color: #212529;
`;

export function AccountSearchListItem({
  account,
  onDelete,
  isFavourite,
  dispatchAccountSearchFavorites,
  dispatchAccountSearchHistory
}: {
  account: dealerSearchIncoming;
  onDelete?: (account: dealerSearchIncoming) => void;
  isFavourite: boolean;
  dispatchAccountSearchFavorites: UseLocalStorageReducerDispatch<dealerSearchIncoming>;
  dispatchAccountSearchHistory: UseLocalStorageReducerDispatch<dealerSearchIncoming>;
}) {
  const { updateSearchRequest } = useContext(AccountSearchContext);

  return (
    <ListItem
      sx={{
        border: 0,
        margin: 0,
        padding: 0
      }}
    >
      <StyledItemContent>
        <StyledAccountName
          onClick={() => {
            updateSearchRequest(account);
            dispatchAccountSearchHistory({
              action: useLocalStorageReducerActions.add,
              payload: account
            });
          }}
        >
          {account.accountTypes && account.accountTypes.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                Account Types:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.accountTypes.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.controlCenterPlatforms &&
            account.controlCenterPlatforms.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  ControlCenter Platforms:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.controlCenterPlatforms.join(", ")}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.countries && account.countries.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                Countries:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.countries.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.created &&
            (account.created.after || account.created.before) && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Created:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.created.after} - {account.created.before}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.franchises &&
            account.franchises.matchAll &&
            account.franchises.matchAll.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Franchises All:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.franchises.matchAll.join(", ")}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.franchises &&
            account.franchises.matchAny &&
            account.franchises.matchAny.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Franchises Any:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.franchises.matchAny.join(", ")}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.franchises &&
            account.franchises.matchNone &&
            account.franchises.matchNone.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Franchises None:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.franchises.matchNone.join(", ")}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.ids && account.ids.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>Ids:</StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.ids.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.names && account.names.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                Names:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.names.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.postalCodes && account.postalCodes.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                Postal Codes:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.postalCodes.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.resources &&
            account.resources.matchAll &&
            account.resources.matchAll.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Resources All:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.resources.matchAll}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.resources &&
            account.resources.matchAny &&
            account.resources.matchAny.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Resources Any:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.resources.matchAny.join(", ")}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.resources &&
            account.resources.matchNone &&
            account.resources.matchNone.length > 0 && (
              <StyledSearchInformation>
                <StyledSearchInformationTitle>
                  Resources None:
                </StyledSearchInformationTitle>
                <StyledSearchInformationValue>
                  {account.resources.matchNone.join(", ")}
                </StyledSearchInformationValue>
              </StyledSearchInformation>
            )}
          {account.states && account.states.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                States:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.states.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.statuses && account.statuses.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                Statuses:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.statuses.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.urls && account.urls.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>Urls:</StyledSearchInformationTitle>

              <StyledSearchInformationValue>
                {account.urls.join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
          {account.verticalTypes && account.verticalTypes.length > 0 && (
            <StyledSearchInformation>
              <StyledSearchInformationTitle>
                Vertical Types:
              </StyledSearchInformationTitle>
              <StyledSearchInformationValue>
                {account.verticalTypes
                  .map(
                    verticalType =>
                      verticalTypeMapping.find(
                        ({ value }) => value === verticalType
                      )?.label || verticalType
                  )
                  .join(", ")}
              </StyledSearchInformationValue>
            </StyledSearchInformation>
          )}
        </StyledAccountName>
        {onDelete && (
          <StyledDeleteButton
            analyticsConfiguration={{
              eventName: "Client Search Delete Account"
            }}
            buttonStyle="aux"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(account);
            }}
          >
            <Tooltip
              position="bottom"
              size="large"
              toolTipContent="Delete from history"
            >
              <TrashIcon title="Delete from history" />
            </Tooltip>
          </StyledDeleteButton>
        )}
        <Button
          analyticsConfiguration={{
            eventName: "Client Search Favorite Account"
          }}
          buttonStyle="aux"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            dispatchAccountSearchFavorites({
              action: useLocalStorageReducerActions.toggle,
              payload: account
            });
          }}
        >
          {isFavourite ? (
            <Tooltip
              position="bottom"
              size="large"
              toolTipContent="Remove favorite"
            >
              <HeartSolidIcon title="Remove favorite" />
            </Tooltip>
          ) : (
            <Tooltip
              position="bottom"
              size="large"
              toolTipContent="Add favorite"
            >
              <HeartIcon title="Add favorite" />
            </Tooltip>
          )}
        </Button>
      </StyledItemContent>
    </ListItem>
  );
}
